// Adjuntos del Chat: qué archivo se acepta y cómo se prepara un archivo de TEXTO.
//
// Todo PURO (sin DOM, sin FileReader) para poder testearlo. La lectura de un `File` de
// verdad vive en `ui/prepararAdjunto.ts`, que es la única parte que necesita el browser.
//
// Desde ADR-2026-09-08-el-chat-toma-el-motor-del-asistente las IMÁGENES ya no viajan en
// base64: suben al storage como en el asistente (`uploadReferenceImage`) y el modelo las
// ve por URL firmada — y además sirven de referencia para generar. Acá queda lo de los
// archivos de texto, que sí van inline: el modelo no tiene herramienta para bajar una
// URL, así que el contenido tiene que ir en el turno.

import type { ChatAttachment } from '../pages/v2/agentAttachments';

/**
 * Cuántos ARCHIVOS (imágenes o textos) VIAJAN en un mismo mensaje, y a partir de qué
 * cantidad lo elegido deja de viajar para quedar como un MONTÓN de fotos que el agente
 * mira a pedido (ADR-2026-09-08-el-chat-trabaja-en-lote y
 * ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez).
 *
 * Queda en SEIS aunque el tope de referencias de una generación subiera a 10
 * (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad): son dos cosas distintas.
 * Seis es lo que una persona engancha a mano en un mensaje, y el resto de las
 * referencias las suman el producto, el modelo, la pose y el fondo que elija de su
 * catálogo — seis archivos más esos cuatro dan justo diez.
 *
 * Pasado el umbral NO se rechaza nada ni se decide por el usuario: las fotos quedan
 * disponibles con su `foto_id` y el agente las mira de a diez con `ver_fotos` y usa las
 * que la instrucción pida. El corte es de TOKENS (seis imágenes ya se arrastran en todo
 * el historial), no de capacidad.
 */
export const MAX_ADJUNTOS = 6;

/** A partir de cuántas imágenes elegidas de una vez quedan como montón para el agente. */
export const UMBRAL_LOTE_FOTOS = MAX_ADJUNTOS + 1;
/** Tope de fotos de un montón por mensaje (80: 16 productos de 5 fotos, o 20 de 4). Espejo de
 *  `MAX_FOTOS_LOTE` de `organizarProductos`. No sube con el tope de 5 por producto: es un
 *  tope del mensaje, no del producto (ADR-2026-09-29-tope-5-fotos-por-producto). */
export const MAX_FOTOS_LOTE = 80;

/** Tope de caracteres de un archivo de texto. Lo que pasa se recorta y se avisa: mejor
 *  un pedazo con aviso que un turno rechazado por tamaño. Y se repite en cada turno
 *  siguiente, porque el historial se manda entero: por eso el tope es bajo. */
export const MAX_CHARS_TEXTO = 20_000;

/** Extensiones de PLANILLA. El `.xlsx` se parsea de verdad (es un ZIP de XMLs, ver
 *  `utils/planilla/xlsx.ts`), así que ya no cae en "binarios que no se pueden leer"
 *  (ADR-2026-09-08-el-agente-lee-y-escribe-planillas). El `.xls` viejo (BIFF, binario de
 *  Excel 97) NO: es otro formato entero y no lo soportamos. */
export const EXTENSIONES_PLANILLA = ['xlsx', 'csv', 'tsv'] as const;

/** PDF. Se lee con pdf.js: el texto si lo tiene, y si está escaneado se renderizan las
 *  páginas como imagen (ADR-2026-09-08-el-agente-lee-y-crea-pdfs). */
export const EXTENSIONES_PDF = ['pdf'] as const;

/**
 * VIDEO de referencia (ADR-2026-09-11-el-agente-entiende-videos-con-gemini). Sube al
 * storage como una imagen, pero el modelo NO lo ve: Claude no recibe video. Lo entiende
 * con `ver_video`, que se lo manda a Gemini desde el backend y devuelve el desglose en
 * texto. Los `.mov` llegan como `video/quicktime`; Gemini los acepta.
 */
export const EXTENSIONES_VIDEO = ['mp4', 'mov', 'webm', 'm4v'] as const;
/** Tope de peso: el mismo que Shorts Studio. Más que eso no sube cómodo desde un browser. */
export const MAX_BYTES_VIDEO = 100 * 1024 * 1024;
/** Tope de duración. Cinco minutos son ~90.000 tokens de Gemini: alcanza para cualquier
 *  reel o anuncio y no se va de costo ni de tiempo de análisis. */
export const MAX_SEGUNDOS_VIDEO = 5 * 60;

export const mensajeVideoGrande = (): string =>
  'El video pesa más de 100 MB. Comprimilo o mandá un recorte más liviano.';
export const mensajeVideoLargo = (): string =>
  'El video dura más de 5 minutos. Mandá un recorte con la parte que querés que mire.';

/** "45 s", "1:23", "12:05". Para el chip y para lo que lee el agente. */
export const formatearDuracion = (seg: number): string => {
  if (!Number.isFinite(seg) || seg <= 0) return '';
  const total = Math.round(seg);
  if (total < 60) return `${total} s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** Extensiones de texto que aceptamos. No entran binarios (docx, pdf, xls viejo): no se
 *  pueden leer como texto y mandarlos crudos sería basura para el modelo. */
export const EXTENSIONES_TEXTO = [
  'txt',
  'md',
  'markdown',
  'csv',
  'tsv',
  'json',
  'log',
] as const;

/** Valor del `accept` del `<input type="file">`. */
export const ACEPTA_ADJUNTOS = `image/*,video/*,${[
  ...new Set([...EXTENSIONES_TEXTO, ...EXTENSIONES_PLANILLA, ...EXTENSIONES_PDF, ...EXTENSIONES_VIDEO]),
]
  .map((e) => `.${e}`)
  .join(',')}`;

export const MENSAJE_FORMATO_ADJUNTO =
  'Solo puedo leer imágenes (JPG, PNG, HEIC), videos (MP4, MOV), PDF, planillas (Excel .xlsx, CSV) y archivos de texto (txt, md, json).';

export const mensajeLimiteAdjuntos = (): string =>
  `Podés mandar hasta ${MAX_ADJUNTOS} archivos por mensaje.`;

export type ClaseDeArchivo = 'imagen' | 'texto' | 'planilla' | 'pdf' | 'video';

/** Extensión en minúsculas, sin el punto. '' si el nombre no tiene. */
export const extensionDe = (nombre: string): string => {
  const limpio = (nombre || '').toLowerCase().trim();
  const punto = limpio.lastIndexOf('.');
  return punto > 0 ? limpio.slice(punto + 1) : '';
};

/**
 * Qué clase de adjunto es un archivo, mirando MIME y extensión. `null` si no lo
 * aceptamos.
 *
 * Se mira la EXTENSIÓN además del MIME porque los `.md` y los `.csv` llegan con `type`
 * vacío en varios navegadores, y un `.json` a veces llega como `application/json` (que
 * no empieza con `text/`).
 */
export const claseDeArchivo = (nombre: string, mime: string): ClaseDeArchivo | null => {
  const tipo = (mime || '').toLowerCase();
  const ext = extensionDe(nombre);
  if (tipo.startsWith('image/')) return 'imagen';
  if (tipo.startsWith('video/') || (EXTENSIONES_VIDEO as readonly string[]).includes(ext)) {
    return 'video';
  }
  // La PLANILLA gana sobre el texto: un `.csv` se parsea como grilla, no se manda crudo.
  // Así el modelo ve columnas y filas en vez de una pared de comas.
  if ((EXTENSIONES_PLANILLA as readonly string[]).includes(ext)) return 'planilla';
  if ((EXTENSIONES_PDF as readonly string[]).includes(ext) || tipo === 'application/pdf')
    return 'pdf';
  if ((EXTENSIONES_TEXTO as readonly string[]).includes(ext)) return 'texto';
  if (tipo.startsWith('text/') || tipo === 'application/json') return 'texto';
  // HEIC/HEIF llegan con `type` vacío en algunos Android; los reconocemos por extensión
  // para que `normalizeImageForUpload` pueda convertirlos.
  if (ext === 'heic' || ext === 'heif') return 'imagen';
  return null;
};

/** Cuántos adjuntos más entran. Nunca negativo. */
export const lugarLibreAdjuntos = (actuales: number): number =>
  Math.max(0, MAX_ADJUNTOS - actuales);

/** ¿Es un ARCHIVO del usuario (imagen, texto, planilla, PDF, video)? Ésos son los que
 *  cuentan para el tope del "+"; un producto, un modelo o una referencia del catálogo
 *  enganchados no ocupan lugar. */
export const esArchivoDelUsuario = (a: ChatAttachment): boolean =>
  a.kind === 'image' ||
  a.kind === 'texto' ||
  a.kind === 'planilla' ||
  a.kind === 'pdf' ||
  a.kind === 'video';

/** Cuántos de los adjuntos son archivos del usuario. */
export const archivosEnganchados = (adjuntos: ChatAttachment[]): number =>
  adjuntos.filter(esArchivoDelUsuario).length;

/**
 * ¿Lo elegido es un MONTÓN de fotos? Sí cuando son `UMBRAL_LOTE_FOTOS` o más y TODAS son
 * imágenes: un montón con un .csv adentro no es un montón, es un error de selección, y se
 * trata como adjuntos normales (que van a rebotar por el tope).
 */
export const esLoteDeFotos = (archivos: Array<{ name: string; type: string }>): boolean =>
  archivos.length >= UMBRAL_LOTE_FOTOS &&
  archivos.every((a) => claseDeArchivo(a.name, a.type) === 'imagen');

/**
 * Los archivos que vienen en un PEGADO (Ctrl/Cmd+V sobre la caja) —
 * ADR-2026-09-13-pegar-archivos-en-la-caja-del-agente.
 *
 * Regla: si el portapapeles trae archivos, los archivos GANAN y el texto que venga con
 * ellos se ignora. Es lo que hace falta para los tres pegados que importan: una captura
 * de pantalla (sólo archivo), una imagen copiada de una web (archivo + un `<img>` en
 * HTML, sin texto útil) y archivos copiados del Finder/Explorador (archivos + sus
 * nombres como texto — pegar los nombres no es lo que se quiso). Si no hay archivos, se
 * devuelve vacío y el pegado sigue su curso normal: texto a la caja.
 *
 * No filtra por clase a propósito: lo que no se acepta rebota en `prepararAdjunto` con
 * el mismo mensaje que si se hubiera elegido con el "+".
 */
export const archivosDelPortapapeles = <A extends { name: string; type: string }>(
  datos: { files?: ArrayLike<A> | null } | null | undefined,
): A[] => Array.from(datos?.files ?? []);

/**
 * Arrastrar y soltar sobre la caja (ADR-2026-09-13-arrastrar-archivos-a-la-caja-del-agente):
 * mismo criterio que el pegado —lo que trae `dataTransfer.files`, en orden, sin filtrar—,
 * y entra por el mismo camino (`engancharArchivos`).
 */
export const archivosArrastrados = <A extends { name: string; type: string }>(
  datos: { files?: ArrayLike<A> | null } | null | undefined,
): A[] => Array.from(datos?.files ?? []);

/**
 * ¿El arrastre trae ARCHIVOS? Durante `dragover` los archivos todavía no se pueden leer
 * (`files` está vacío hasta el `drop`); lo único que dice qué se está arrastrando es
 * `types`. Sin este chequeo, arrastrar un texto seleccionado o un link también prendería
 * el "soltá acá".
 */
export const traeArchivos = (datos: { types?: ArrayLike<string> | null } | null | undefined): boolean =>
  Array.from(datos?.types ?? []).includes('Files');

export const mensajeLoteFotos = (n: number): string =>
  n > MAX_FOTOS_LOTE
    ? `Tomé las primeras ${MAX_FOTOS_LOTE} fotos: es el máximo por mensaje.`
    : `${n} fotos listas. Decile a Atlas qué hacer con ellas, o mandá directo y las organiza en productos.`;

/** Peso legible para el chip. Sin decimales en KB, uno en MB. */
export const formatearPeso = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

/**
 * El contenido de un archivo de texto listo para viajar: recortado al tope, con el aviso
 * de que seguía. `null` si está vacío (un archivo vacío no aporta nada al turno).
 */
export function recortarTexto(crudo: string): string | null {
  const recortado = crudo.length > MAX_CHARS_TEXTO;
  const datos = recortado
    ? `${crudo.slice(0, MAX_CHARS_TEXTO)}\n\n[…recortado: el archivo seguía, pero solo entra este pedazo.]`
    : crudo;
  return datos.trim() ? datos : null;
}
